import { c as _c } from "react/compiler-runtime";
import type { CalendarDate } from '@internationalized/date';
import { isSameDay } from '@internationalized/date';
import type { PointerEventHandler, RefObject } from 'react';
import { useHover } from 'react-aria';
import type { useDateRangePickerConfig } from '../date-range-picker/use-date-range-picker-config';
import { calendarDateToDate } from './to-calendar-date';
import { useCell } from './use-cell';
type Config = Pick<ReturnType<typeof useDateRangePickerConfig>, 'state' | 'onChange' | 'isMobile' | 'pinnedAnchorDate' | 'setPinnedAnchorDate' | 'initialHideRangeMarkers' | 'allowsNonContiguousRanges'> & {
  date: CalendarDate;
  currentMonth: CalendarDate;
};
export const useRangeCell = (t0, ref) => {
  const $ = _c(85);
  const {
    state,
    date,
    currentMonth,
    pinnedAnchorDate,
    setPinnedAnchorDate,
    allowsNonContiguousRanges,
    initialHideRangeMarkers,
    isMobile,
    onChange
  } = t0;
  let t1;
  if ($[0] !== currentMonth || $[1] !== date || $[2] !== state) {
    t1 = {
      state,
      date,
      currentMonth
    };
    $[0] = currentMonth;
    $[1] = date;
    $[2] = state;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    cell,
    isFocusVisible,
    isDisabledOrUnavailable,
    isTodayCell,
    isOutsideMonth,
    mergedButtonProps,
    cellProps: baseCellProps
  } = useCell(t1, ref);
  const {
    isInvalid,
    isUnavailable,
    isDisabled,
    isOutsideVisibleRange,
    isSelected: selected
  } = cell;
  const isSelected = selected && !pinnedAnchorDate;
  const hideRangeMarkers = initialHideRangeMarkers || isInvalid;
  let t2;
  if ($[4] !== date || $[5] !== isSelected || $[6] !== state.highlightedRange) {
    t2 = state.highlightedRange ? isSameDay(date, state.highlightedRange.start) : isSelected;
    $[4] = date;
    $[5] = isSelected;
    $[6] = state.highlightedRange;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const isSelectionStart = t2;
  let t3;
  if ($[8] !== date || $[9] !== isSelected || $[10] !== state.highlightedRange) {
    t3 = state.highlightedRange ? isSameDay(date, state.highlightedRange.end) : isSelected;
    $[8] = date;
    $[9] = isSelected;
    $[10] = state.highlightedRange;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const isSelectionEnd = t3;
  const isSelectedEdge = isSelectionStart || isSelectionEnd;
  let t4;
  if ($[12] !== date || $[13] !== hideRangeMarkers || $[14] !== isSelectedEdge || $[15] !== pinnedAnchorDate) {
    t4 = !hideRangeMarkers && (pinnedAnchorDate ? isSameDay(date, pinnedAnchorDate) : isSelectedEdge);
    $[12] = date;
    $[13] = hideRangeMarkers;
    $[14] = isSelectedEdge;
    $[15] = pinnedAnchorDate;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const isSelectedMarker = t4;
  const isHighlightedAndUnavailable = !pinnedAnchorDate && allowsNonContiguousRanges && isUnavailable && state.highlightedRange && date.compare(state.highlightedRange.start) > 0 && date.compare(state.highlightedRange.end) < 0;
  const isHighlighted = isSelected || isHighlightedAndUnavailable;
  const isHighlightedEdge = !hideRangeMarkers && !pinnedAnchorDate;
  let t5;
  if ($[17] !== state.highlightedRange) {
    t5 = state.highlightedRange && isSameDay(state.highlightedRange.start, state.highlightedRange.end);
    $[17] = state.highlightedRange;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const isHighlightedSameDay = t5;
  const t6 = !isMobile && !pinnedAnchorDate && !isOutsideMonth && !isDisabled && !isUnavailable;
  let t7;
  if ($[19] !== date || $[20] !== isDisabled || $[21] !== isDisabledOrUnavailable || $[22] !== isOutsideMonth || $[23] !== isOutsideVisibleRange || $[24] !== isUnavailable || $[25] !== onChange || $[26] !== pinnedAnchorDate || $[27] !== setPinnedAnchorDate || $[28] !== state) {
    t7 = () => {
      if (state.anchorDate && !pinnedAnchorDate && (isOutsideMonth || isDisabledOrUnavailable)) {
        onChange([calendarDateToDate(state.anchorDate), null]);
        setPinnedAnchorDate(state.anchorDate);
      }
      if (pinnedAnchorDate && !isOutsideVisibleRange && !isOutsideMonth && !isDisabled && !isUnavailable) {
        state.setFocusedDate(date);
        setPinnedAnchorDate(null);
      }
    };
    $[19] = date;
    $[20] = isDisabled;
    $[21] = isDisabledOrUnavailable;
    $[22] = isOutsideMonth;
    $[23] = isOutsideVisibleRange;
    $[24] = isUnavailable;
    $[25] = onChange;
    $[26] = pinnedAnchorDate;
    $[27] = setPinnedAnchorDate;
    $[28] = state;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== t6 || $[31] !== t7) {
    t8 = {
      isDisabled: t6,
      onHoverStart: t7
    };
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const {
    hoverProps
  } = useHover(t8);
  let t9;
  if ($[33] !== hideRangeMarkers || $[34] !== isDisabledOrUnavailable || $[35] !== isInvalid || $[36] !== isSelected || $[37] !== isSelectedEdge) {
    t9 = () => {
      const isEdgeSelected = hideRangeMarkers && isSelectedEdge;
      const isNonEdgeSelected = isSelected && !isSelectedEdge;
      return !isInvalid && (isEdgeSelected || isNonEdgeSelected) || !isDisabledOrUnavailable;
    };
    $[33] = hideRangeMarkers;
    $[34] = isDisabledOrUnavailable;
    $[35] = isInvalid;
    $[36] = isSelected;
    $[37] = isSelectedEdge;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const isCellHovered = t9;
  let t10;
  if ($[39] !== date || $[40] !== isDisabledOrUnavailable || $[41] !== isMobile || $[42] !== mergedButtonProps || $[43] !== onChange || $[44] !== pinnedAnchorDate || $[45] !== setPinnedAnchorDate || $[46] !== state.anchorDate) {
    t10 = event => {
      if (!isMobile && !isDisabledOrUnavailable && !pinnedAnchorDate && !state.anchorDate) {
        onChange([calendarDateToDate(date), null]);
        setPinnedAnchorDate(date);
      }
      mergedButtonProps.onPointerDown?.(event);
    };
    $[39] = date;
    $[40] = isDisabledOrUnavailable;
    $[41] = isMobile;
    $[42] = mergedButtonProps;
    $[43] = onChange;
    $[44] = pinnedAnchorDate;
    $[45] = setPinnedAnchorDate;
    $[46] = state.anchorDate;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  const onPointerDown = t10;
  let t11;
  if ($[48] !== date || $[49] !== isDisabledOrUnavailable || $[50] !== isMobile || $[51] !== mergedButtonProps || $[52] !== onChange || $[53] !== pinnedAnchorDate || $[54] !== setPinnedAnchorDate || $[55] !== state.anchorDate) {
    t11 = event_0 => {
      mergedButtonProps.onPointerUp?.(event_0);
      if (isMobile && !isDisabledOrUnavailable && !pinnedAnchorDate && !state.anchorDate) {
        onChange([calendarDateToDate(date), null]);
        setPinnedAnchorDate(date);
      }
    };
    $[48] = date;
    $[49] = isDisabledOrUnavailable;
    $[50] = isMobile;
    $[51] = mergedButtonProps;
    $[52] = onChange;
    $[53] = pinnedAnchorDate;
    $[54] = setPinnedAnchorDate;
    $[55] = state.anchorDate;
    $[56] = t11;
  } else {
    t11 = $[56];
  }
  const onPointerUp = t11;
  let t12;
  if ($[57] !== isCellHovered || $[58] !== isMobile) {
    t12 = !isMobile && isCellHovered();
    $[57] = isCellHovered;
    $[58] = isMobile;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== hideRangeMarkers || $[61] !== isDisabledOrUnavailable || $[62] !== isFocusVisible || $[63] !== isHighlighted || $[64] !== isHighlightedEdge || $[65] !== isHighlightedSameDay || $[66] !== isOutsideMonth || $[67] !== isSelectedMarker || $[68] !== isSelectionEnd || $[69] !== isSelectionStart || $[70] !== isTodayCell || $[71] !== t12) {
    t13 = {
      isFocusVisible,
      isOutsideMonth,
      isDisabledOrUnavailable,
      isSelectedMarker,
      isSelectionStart,
      isSelectionEnd,
      isHighlighted,
      isHighlightedEdge,
      isHighlightedSameDay,
      isHovered: t12,
      isTodayCell,
      hideRangeMarkers
    };
    $[60] = hideRangeMarkers;
    $[61] = isDisabledOrUnavailable;
    $[62] = isFocusVisible;
    $[63] = isHighlighted;
    $[64] = isHighlightedEdge;
    $[65] = isHighlightedSameDay;
    $[66] = isOutsideMonth;
    $[67] = isSelectedMarker;
    $[68] = isSelectionEnd;
    $[69] = isSelectionStart;
    $[70] = isTodayCell;
    $[71] = t12;
    $[72] = t13;
  } else {
    t13 = $[72];
  }
  let t14;
  if ($[73] !== baseCellProps || $[74] !== hoverProps) {
    t14 = {
      ...baseCellProps,
      ...hoverProps
    };
    $[73] = baseCellProps;
    $[74] = hoverProps;
    $[75] = t14;
  } else {
    t14 = $[75];
  }
  let t15;
  if ($[76] !== mergedButtonProps || $[77] !== onPointerDown || $[78] !== onPointerUp) {
    t15 = {
      ...mergedButtonProps,
      onPointerDown,
      onPointerUp
    };
    $[76] = mergedButtonProps;
    $[77] = onPointerDown;
    $[78] = onPointerUp;
    $[79] = t15;
  } else {
    t15 = $[79];
  }
  let t16;
  if ($[80] !== cell || $[81] !== t13 || $[82] !== t14 || $[83] !== t15) {
    t16 = {
      cell,
      conditions: t13,
      cellProps: t14,
      buttonProps: t15
    };
    $[80] = cell;
    $[81] = t13;
    $[82] = t14;
    $[83] = t15;
    $[84] = t16;
  } else {
    t16 = $[84];
  }
  return t16;
};